/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import React from 'react';
import type { ApiModule, ApiUrls } from '@opengeoweb/api';
import { ErrorBoundary } from '@opengeoweb/shared';
import type { ForecastType } from '../../types';
import { TafModule } from '../TafModule';

export const tafScreenPreset = {
  id: 'screenConfigTaf',
  title: 'TAF',
  views: {
    byId: {
      taf: {
        title: 'TAF',
        componentType: 'TafModule',
        initialProps: {},
      },
    },
    allIds: ['taf'],
  },
  syncGroups: [],
  mosaicNode: 'taf',
};

type TafComponentType = 'TafModule';

interface InitialProps {
  isAutoTafEnabled?: boolean;
  forecastType?: ForecastType;
}

export interface ComponentsLookUpPayload {
  componentType: TafComponentType;
  id: string;
  title?: string;
  initialProps: InitialProps;
}

// base type for connected modules
export interface ApiModuleProps extends ApiModule {
  config: ApiUrls;
  onPreventCloseView?: (shouldPreventClose: boolean) => void;
  initialProps?: InitialProps;
}

export const TafApiWrapper: React.FC<ApiModuleProps> = ({
  config,
  onPreventCloseView,
  initialProps,
}: ApiModuleProps) => {
  return config ? (
    <ErrorBoundary>
      <TafModule
        forecastType={initialProps?.forecastType}
        isAutoTafEnabled={initialProps?.isAutoTafEnabled}
        onPreventCloseView={onPreventCloseView}
      />
    </ErrorBoundary>
  ) : null;
};

export const componentsLookUp = (
  payload: ComponentsLookUpPayload,
  { config, auth, onSetAuth }: ApiModuleProps,
  // TODO: find a good way to type this module without the apps complaining
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ConnectedModuleWrapper: any = null,
): React.ReactElement => {
  const { componentType, initialProps } = payload;
  switch (componentType) {
    case 'TafModule':
      if (ConnectedModuleWrapper) {
        return (
          <ConnectedModuleWrapper
            auth={auth}
            component={(props: ApiModuleProps) => (
              <TafApiWrapper {...props} initialProps={initialProps} />
            )}
            config={config}
            data-testid="tafmodule"
            onSetAuth={onSetAuth}
            payload={payload}
          />
        );
      }
      return (
        <TafApiWrapper
          auth={auth}
          config={config}
          data-testid="tafmodule"
          initialProps={initialProps}
          onSetAuth={onSetAuth}
        />
      );
    default:
      return null!;
  }
};
